/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * The component for list account for AccountSelect component
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.3.0
 * @since 1.2.0
 */

import { RefObject, useEffect, useRef, useState } from 'react';

import Highlighter from 'react-highlight-words';
import InfiniteScroll from 'react-infinite-scroller';

import ICommonTranslation from '../../modules/internationalization/types/common';
import { accountSelect } from '../../theme';
import { formatBalance } from '../../utils/formatText';
import { EllipseText } from '../common';
import { IAccountSelectItem } from './AccountSelectTypes';
import {
    AccountSelectListBodyStyled,
    AccountSelectListHeaderStyled,
    AccountSelectListItemStyled,
    AccountSelectListRowStyled,
    AccountSelectListStyled,
} from './styled/AccountSelectStyled';

export interface IAccountSelectListProps {
    data: IAccountSelectItem[];
    hasMore: boolean;
    onScrollAccountList: () => void;
    onSelectAccount: (item: IAccountSelectItem) => void;
    searchValue: string;
    translation: ICommonTranslation;
}

export const AccountSelectList = (props: IAccountSelectListProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isListOverflown, setListOverflown] = useState(false);
    const listBodyRef: RefObject<HTMLDivElement> = useRef(null);

    const debouncedLoadMore = () => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        props.onScrollAccountList();
    };

    useEffect(() => {
        setIsLoading(false);
    }, [props.data.length]);

    useEffect(() => {
        setListOverflown(!!listBodyRef.current && listBodyRef.current.scrollHeight > listBodyRef.current.clientHeight)
    }, [listBodyRef.current?.scrollHeight]);

    return (
        <AccountSelectListStyled>
            <AccountSelectListHeaderStyled>
                <AccountSelectListItemStyled
                    width={accountSelect.listItemNumberWidth}
                >
                    {props.translation.accountSelect.account}
                </AccountSelectListItemStyled>
                <AccountSelectListItemStyled>
                    {props.translation.accountSelect.name}
                </AccountSelectListItemStyled>
                <AccountSelectListItemStyled
                    textAlign='right'
                    width={accountSelect.listItemBalanceWidth}
                >
                    {props.translation.accountSelect.balance}
                </AccountSelectListItemStyled>
            </AccountSelectListHeaderStyled>

            <AccountSelectListBodyStyled
                ref={listBodyRef}
            >
                <InfiniteScroll
                    element={'ul'}
                    hasMore={props.hasMore}
                    loadMore={debouncedLoadMore}
                    pageStart={0}
                    threshold={48}
                    useWindow={false}
                >
                    {
                        props.data.map(item => (
                            <AccountSelectListRowStyled
                                data-index={item.id}
                                isPaddingRight={!isListOverflown}
                                key={item.id}
                                onClick={() => props.onSelectAccount(item)}
                            >
                                <AccountSelectListItemStyled width={accountSelect.listItemNumberWidth}>
                                    <Highlighter
                                        autoEscape={true}
                                        highlightClassName="account-select__list-item--text-bold"
                                        searchWords={[props.searchValue]}
                                        textToHighlight={item.number || ''}
                                    />
                                </AccountSelectListItemStyled>
                                <EllipseText
                                    title={item.name}
                                    wrapper={AccountSelectListItemStyled}
                                >
                                    {item.name}
                                </EllipseText>
                                <EllipseText
                                    title={item.name}
                                    wrapper={AccountSelectListItemStyled}
                                    wrapperProps={{
                                        width: accountSelect.listItemBalanceWidth,
                                        textAlign: 'right',
                                    }}
                                >
                                    {formatBalance(item.balance)} {props.translation.currency}
                                </EllipseText>
                            </AccountSelectListRowStyled>))
                    }
                </InfiniteScroll>
            </AccountSelectListBodyStyled>
        </AccountSelectListStyled>
    )
};
